'use client';

import { useEffect, useMemo, useState } from 'react';

import { Autocomplete, AutocompleteItem } from '@heroui/react';

import { useDetectCountry } from '@/src/queries/country/use-detect-country';
import { getCountryOptions } from '@/utils/currency';

import './phoneCountry.css';

interface PhoneCountryProps {
  selectedCountry: string;
  onSelectCountry: (country: string, phonePrefix?: string) => void;
  // eslint-disable-next-line react/require-default-props
  selectedPhonePrefix?: string;
  // eslint-disable-next-line react/require-default-props
  theme?: 'light' | 'dark';
  // eslint-disable-next-line react/require-default-props
  height?: 'thin' | 'regular';
  // eslint-disable-next-line react/require-default-props
  autoDetect?: boolean;
  // eslint-disable-next-line react/require-default-props
  showPhonePrefix?: boolean;
}

const prefixDigits = (phonePrefix?: string): string => {
  return phonePrefix?.replace(/\D/g, '') || '';
};

export const PhoneCountry = ({
  selectedCountry,
  onSelectCountry,
  selectedPhonePrefix,
  theme = 'dark',
  height = 'regular',
  autoDetect = false,
  showPhonePrefix = false,
}: PhoneCountryProps) => {
  const countries = getCountryOptions();
  const { data } = useDetectCountry();
  const prefixItems = countries.flatMap((country) => {
    const prefixes = country.phonePrefixes ?? [country.phonePrefix];
    return prefixes.map((phonePrefix) => {
      return {
        ...country,
        phonePrefix,
        key: `${country.code}:${prefixDigits(phonePrefix)}`,
      };
    });
  });
  const items = showPhonePrefix ? prefixItems : countries;
  const [hasDetected, setHasDetected] = useState(false);

  const selectedCountryData = countries.find((country) => {
    return country.code === selectedCountry;
  });

  const currentPrefix = selectedPhonePrefix || prefixDigits(selectedCountryData?.phonePrefix);

  useEffect(() => {
    if (autoDetect && !hasDetected && !selectedCountry) {
      onSelectCountry(data?.countryCode || '');
      setHasDetected(true);
    }
  }, [autoDetect, hasDetected, onSelectCountry, selectedCountry, data?.countryCode]);

  const displayValue = useMemo(() => {
    if (!selectedCountryData) {
      return '';
    }
    if (showPhonePrefix) {
      return `${selectedCountryData.flag} +${currentPrefix}`;
    }
    return `${selectedCountryData.flag} ${selectedCountryData.name}`;
  }, [selectedCountryData, showPhonePrefix, currentPrefix]);

  const [inputValue, setInputValue] = useState(displayValue);

  useEffect(() => {
    setInputValue(displayValue);
  }, [displayValue]);

  return (
    <Autocomplete
      className={`w-full rounded-lg! ${height === 'thin' ? 'h-12!' : 'h-16!'}`}
      classNames={{
        listboxWrapper: [`autocomplete-listbox-wrapper autocomplete-listbox-wrapper--${theme}`],
        listbox: [`autocomplete-listbox autocomplete-listbox--${theme}`],
        popoverContent: [`autocomplete-popover autocomplete-popover--${theme} w-auto!`],
        clearButton: ['hidden!']
      }}
      inputValue={inputValue}
      items={items}
      inputProps={{
        classNames: {
          input: [`autocomplete-input autocomplete-input--${theme} w-full`],
          inputWrapper: [`autocomplete-wrapper autocomplete-wrapper--${theme}`, 'data-[focus=true]:border-secondary-500!', 'data-[hover=true]:border-secondary-400!', 'focus-within:border-secondary-500!'],
          innerWrapper: ['bg-transparent', 'bg-transparent!', 'data-[focus=true]:bg-transparent!', 'data-[hover=true]:bg-transparent!']
        },
        placeholder: hasDetected || selectedCountry ? 'Selecciona tu país' : 'Detectando país...'
      }}
      radius="lg"
      selectedKey={showPhonePrefix ? `${selectedCountry}:${currentPrefix}` : (selectedCountry || null)}
      onInputChange={(value) => {
        setInputValue(value);
      }}
      onSelectionChange={(key) => {
        if (typeof key !== 'string') {
          return;
        }

        const activeKey = showPhonePrefix ? `${selectedCountry}:${currentPrefix}` : selectedCountry;
        if (key === activeKey) {
          return;
        }

        if (showPhonePrefix) {
          const [code, prefixFromKey] = key.split(':');
          const selected = prefixItems.find((item) => { return item.key === key; });
          const nextPrefix = prefixFromKey || prefixDigits(selected?.phonePrefix);
          if (!code || !nextPrefix) {
            return;
          }
          const flag = selected?.flag || selectedCountryData?.flag || '';
          setInputValue(`${flag} +${nextPrefix}`);
          onSelectCountry(code, nextPrefix);
          return;
        }

        const selected = countries.find((item) => { return item.code === key; });
        if (selected) {
          setInputValue(`${selected.flag} ${selected.name}`);
        }
        onSelectCountry(key);
      }}
    >
      {(item) => {
        return (
          <AutocompleteItem
            key={showPhonePrefix ? `${item.code}:${prefixDigits(item.phonePrefix)}` : item.code}
            className={`autocomplete-item autocomplete-item--${theme}`}
            textValue={showPhonePrefix ? `${item.flag} +${prefixDigits(item.phonePrefix)}` : `${item.flag} ${item.name} ${item.currency}`}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">{item.flag}</span>
              <span className="flex-1">{item.name}</span>
              <span className="text-xs opacity-70">{showPhonePrefix ? item.phonePrefix || '' : item.currency}</span>
            </div>
          </AutocompleteItem>
        );
      }}
    </Autocomplete>
  );
};
